import React, {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router";
import {useTranslation} from "react-i18next";
import Api from "../../services/Api";
import {getLanguage} from "../../lang/helpers";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";
import RequestStatus from "../../components/RequestStatus/RequestStatus";
import iconPlus2 from "url:shared-assets/icons/white/plus.svg";
import iconLock from "url:shared-assets/icons/lock.svg";
import iconArrowBent from "url:shared-assets/icons/arrow-bent.svg";
import iconLightning from "url:shared-assets/icons/lightning.svg";
import iconDown from "url:shared-assets/icons/chevronDown.svg";
import iconPencilBare from "url:shared-assets/icons/pencil-bare.svg";
import iconTrash from "url:shared-assets/icons/trash.svg";
import iconAi from "url:shared-assets/icons/black/ai.svg";
import iconLeft from "url:shared-assets/icons/chevronLeft.svg";
import iconCheck from "url:shared-assets/icons/checkmark.svg";
import iconClock from "url:shared-assets/icons/clock.svg";
import iconDevelopmentPlan from "url:shared-assets/icons/developmentPlan.svg";
import iconCheckCircle from "url:shared-assets/icons/check-circle.svg";
import iconCircleRings from "url:shared-assets/icons/circle-rings.svg";
import iconCheckCircleGreen from "url:shared-assets/icons/success-500/check-circle.svg";
import iconInfoFilled from "url:shared-assets/icons/primary-500/info-filled.svg";
import iconScale from "url:shared-assets/icons/scale.svg";
import iconEye from "url:shared-assets/icons/eye.svg";
import iconEmail from "url:shared-assets/icons/email_16x16.svg";
import iconEmailOpen from "url:shared-assets/icons/email-open.svg";
import iconCalendar from "url:shared-assets/icons/calendar.svg";
import iconAccount from "url:shared-assets/icons/account.svg";
import iconNotes from "url:shared-assets/icons/notes.svg";
import iconRightWhite from "url:shared-assets/icons/white/arrow-right.svg";
import iconNotesBlack from "url:shared-assets/icons/black/notes.svg";
import iconFlagBlack from "url:shared-assets/icons/black/flag.svg";
import iconPuzzleBlack from "url:shared-assets/icons/black/puzzle.svg";
import iconRightBlack from "url:shared-assets/icons/black/arrow-right.svg";
import iconCalendar2 from "url:shared-assets/icons/calendar-2.svg";
import iconProgress from "url:shared-assets/icons/progress.svg";
import iconClose from "url:shared-assets/icons/close.svg";
import iconRight from "url:shared-assets/icons/black/arrow-right.svg";
import {
  EditPart,
  VisionStatementAction,
  VisionStatementActivity,
  VisionStatementGoal,
  VisionStatementBehavior,
  VisionStatementInterface,
  VisionStatementPartStatus,
  VisionStatementStatus,
  VsSurveyRequest,
  AwarenessInterface,
  BarrierInterface,
} from "./interfaces";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../components/Modal";
import DotsPopup from "../../components/Popup/DotsPopup"
import CustomButton from "../../components/CustomButton";
import Checkbox from "../../components/Checkbox";
import PlainBadge from "../../components/Badges/PlainBadge";
import TwoBars from "../../components/Bars/TwoBars";
import TabMenu from "../../components/TabMenu/TabMenu";
import {getTabNr, setTabNr} from "../../helpers/tabNavigation";
import SidePanel from "../../components/SidePanel/SidePanel";
import UpdateProgress from "./components/UpdateProgress";
import UpdateGoalStatus from "./components/UpdateGoalStatus";
import LongTermGoalPart from "./components/LongTermGoalPart";
import SurveyQuestionsResult from "../SurveyResult/components/SurveyQuestionsResult";
import {renderOnTrack2} from "./helpers";
import {storeAiStart} from "../../helpers/ai_thread";
import {attentionTxt} from "../../helpers/misc_texts";
import {StrMap} from "../../interfaces/common";
import {FeedbackType, SurveyQuestionInterface, SurveyResultInterface} from "../SurveyResult/interfaces";
import ClientAwareness from "./components/ClientAwareness";
import ClientBarriers from "./components/ClientBarriers";
import ChatThreadInput from "../AiThreads/components/ChatThreadInput";
import {useSetAtom} from 'jotai';
import {toastAtom} from '../../jotai_state';
import AttentionBox from "../../components/Boxes/AttentionBox";


interface DeletePart {
  id: string;
  type: string;
}

interface VisionStatementChat {
  id: string;
  topic: string;
}

const LongTermGoal = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(getTabNr());
  const [updateProgress, setUpdateProgress] = useState("");
  const [updateGoalStatus, setUpdateGoalStatus] = useState(false);
  const [chatThreadMap, setChatThreadMap] = useState<StrMap>({});
  const [goals, setGoals] = useState<VisionStatementGoal[]>([]);
  const [behaviors, setBehaviors] = useState<VisionStatementBehavior[]>([]);
  const [activities, setActivities] = useState<VisionStatementActivity[]>([]);
  const [viewGoal, setViewGoal] = useState<VisionStatementGoal | null>(null);
  const [viewBehavior, setViewBehavior] = useState<VisionStatementBehavior | null>(null);
  const [viewAction, setViewAction] = useState<VisionStatementAction | null>(null);
  const [viewActivity, setViewActivity] = useState<VisionStatementActivity | null>(null);
  const [editPart, setEditPart] = useState<EditPart | null>(null);
  const [deletePart, setDeletePart] = useState<DeletePart | null>(null);
  const [hideAwareness, setHideAwareness] = useState(false);
  const [barriersOpen, setBarriersOpen] = useState(false);
  const [showDeleteBarrier, setShowDeleteBarrier] = useState<BarrierInterface | null>(null);
  const [surveyRequestNr, setSurveyRequestNr] = useState(0);
  const [clientName, setClientName] = useState("");
  const [clientId, setClientId] = useState("");
  const [visionStatementChats, setVisionStatementChats] = useState<Array<VisionStatementChat>>([]);
  const [visionStatement, setVisionStatement] = useState<VisionStatementInterface>({
    id: "",
    surveyId: null,
    title: "",
    description: "",
    deadline: "",
    activated: "",
    progress: 0,
    timeProgress: 0,
    development: 0,
    effort: 0,
    onTrack: 0,
    status: 0,
    avgGap: 0,
    questions: [],
    surveyRequests: [],
    customerCategoryId: "",
    goals: 0,
    activities: 0,
    behaviors: 0,
    subItemsDue: 0,
    barriersDone: false,
    ownSurvey: false,
    snapshotDone: false,
    translations_answers:{},
    updated: "",
  });
  const [awareness, setAwareness] = useState<AwarenessInterface | undefined>();
  const [barriers, setBarriers] = useState<Array<BarrierInterface>>([]);
  const [coachStyle, setCoachStyle] = useState<string>("coaching");
  const [selectAction, setSelectAction] = useState(false);
  const setGlobalToast = useSetAtom(toastAtom);
  const { visionStatementId } = useParams();
  const { hash } = useLocation();
  const today = new Date();
  const todayDate = today.toISOString().substring(0, 10);
  const coachView = window.location.pathname.startsWith("/clients/long_term_goal/");

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    // When we reload data after a change of goal or action, check if we need to update viewGoal or viewAction
    if (viewGoal) {
      let foundGoal = false;
      for (let goal of goals) {
        if (viewGoal.id === goal.id) {
          setViewGoal(goal);
          foundGoal = true;
          if (viewAction) {
            let found = false;
            for (let action of goal.actions) {
              if (viewAction.id === action.id) {
                setViewAction(action);
                found = true;
                break;
              }
            }
            if (!found) {
              // Assume it is deleted
              setViewAction(null);
            }
          }
          break;
        }
      }
      if (!foundGoal) {
        // Assume it was deleted
        setViewGoal(null);
      }
    } else if (viewActivity) {
      let found = false;
      for (let activity of activities) {
        if (viewActivity.id === activity.id) {
          setViewActivity(activity);
          found = true;
        }
      }
      if (!found) {
        // Assume it was deleted
        setViewActivity(null);
      }
    } else if (viewBehavior) {
      let found = false;
      for (let behavior of behaviors) {
        if (viewBehavior.id === behavior.id) {
          setViewBehavior(behavior);
          found = true;
        }
      }
      if (!found) {
        // Assume it was deleted
        setViewBehavior(null);
      }
    }
  }, [goals, activities, behaviors])

  useEffect(() => {
    if (!loading) {
      if (hash) {
        navigateHash();
      }
    }
  }, [loading]);

  const changeTab = (_tab: number) => {
    setTab(_tab);
    setTabNr(_tab);
    if (_tab > 0) {
      setViewGoal(null);
      setViewAction(null);
      setViewActivity(null);
      setViewBehavior(null);
    }
  };

  const vsChat = (_coachStyle: string, _temp: string, msg: string) => {
    toAiThread("vision_statement", visionStatement.id, msg);
  }

  const toAiThread = (originName: string, originId: string, msg?: string) => {
    let threadKey = originName + "_" + originId;
    let threadId = chatThreadMap[threadKey];
    let _coachStyle = "coaching";
    if (threadId) {
      navigate('/ai/' + threadId);
    } else {
      let message: string | null = null;
      if (originName === "vision_statement_goal" && viewGoal) {
        message = lang === "sv" ? "Jag vill prata om mitt delmål: " : "I want to talk about my Sub-goal: "
        message += viewGoal.title
      } else if (originName === "vision_statement_action" && viewAction) {
        message = lang === "sv" ? "Jag vill prata om min uppgift: " : "I want to talk about my action: "
        message += viewAction.title
      } else if (originName === "vision_statement_activity" && viewActivity) {
        message = lang === "sv" ? "Jag vill prata om min aktivitet: " : "I want to talk about my activity: "
        message += viewActivity.title;
      } else if (originName === "vision_statement_behavior" && viewBehavior) {
        message = lang === "sv" ? "Jag vill prata om mitt beteende: " : "I want to talk about my behavior: "
        message += viewBehavior.title;
      } else if (originName === "vision_statement") {
        if (msg) {
          message = msg;
        }
        _coachStyle = coachStyle;
      }
      storeAiStart(_coachStyle, "", null, null, null, message, originName, originId);
      navigate("/ai/0");
    }
  };

  const openGoal = (goalId: string) => {
    for (let goal of goals) {
      if (goal.id === goalId) {
        setViewGoal(goal);
        return;
      }
    }
  }

  const openActivity = (activityId: string) => {
    for (let activity of activities) {
      if (activity.id === activityId) {
        setViewActivity(activity);
        return;
      }
    }
  }

  const openBehavior = (behaviorId: string) => {
    for (let behavior of behaviors) {
      if (behavior.id === behaviorId) {
        setViewBehavior(behavior);
        return;
      }
    }
  }

  const openAction = (actionId: string) => {
    for (let goal of goals) {
      for (let action of goal.actions) {
        if (action.id === actionId) {
          setViewGoal(goal);
          setViewAction(action);
          return;
        }
      }
    }
  }

  const navigateHash = () => {
    if (hash.startsWith("#goal_")) {
      openGoal(hash.substring(6));
    } else if (hash.startsWith("#activity_")) {
      openActivity(hash.substring(10));
    } else if (hash.startsWith("#action_")) {
      openAction( hash.substring(8));
    } else if (hash.startsWith("#behavior_")) {
      openBehavior( hash.substring(10));
    }
  }

  const getData = () => {
    const url = coachView ? "coach/client/vision_statement/" : "client/vision_statement/";
    api.get(url + visionStatementId).then(response => {
      if (response.json.status === "ok") {
        const _visionStatement: VisionStatementInterface = response.json.visionStatement;
        setVisionStatement(_visionStatement);
        setGoals(response.json.goals);
        setBehaviors(response.json.behaviors);
        setAwareness(response.json.awareness);
        setBarriers(response.json.barriers);
        setChatThreadMap(response.json.chatThreadMap);
        setActivities(response.json.activities);
        setClientName(response.json.clientName);
        setClientId(response.json.clientId);
        setVisionStatementChats(response.json.visionStatementChats);
        if (_visionStatement.status < VisionStatementStatus.Created) {
          setEditPart({
            id: _visionStatement.id,
            type: "vision_statement",
            title: _visionStatement.title,
            description: _visionStatement.description,
            deadline: _visionStatement.deadline,
          });
        }
        if (_visionStatement.barriersDone) {
          setHideAwareness(true);
        }
      } else if (response.json.error) {
        setError(response.json.error);
      } else {
        setError("Unknown error");
      }
      setLoading(false);
    });
  }

  if (loading) {
    return (<div className="mt-4">Loading...</div>);
  }

  if (error.length > 0) {
    return (<div className="mt-4">Error: {error}</div>);
  }

  const saveVisionStatement = (title: string, description: string, deadline: string) => {
    const _visionStatement = {...visionStatement, title, description, deadline};
    let created = false;
    if (_visionStatement.progress < 10) {
      _visionStatement.progress = 10;
      _visionStatement.onTrack = 1;
      created = true;
      setGlobalToast({
        position: "top-center",
        type: "info",
        infoType: "created",
        text: lang === "sv" ? "Målbild skapad" : "Goal created",
      });
    } else {
      setGlobalToast({
        text: lang === "sv" ? "Ändringar sparade" : "Changes saved",
      });
    }
    const data = {
      cmd: "save",
      visionStatement: _visionStatement
    }

    api.post("client/vision_statement_update/" + visionStatement.id, data).then(response => {
      if (response.json.status === "ok") {
        if (created) {
          // Just created a new Goal. Redirect to dev-plan
          navigate("/dev-plan");
        } else {
          _visionStatement.status = VisionStatementStatus.Created;
          setVisionStatement(_visionStatement);
          setEditPart(null);
        }

      }
    })
  }

  const showVisionStatementPartToast = () => {
    if (!editPart) return;
    let largeToast = false;
    let msg = "";

    if (editPart.id.length > 2) {
      msg = lang === "sv" ? "Ändringar sparade" : "Changes saved";
    } else {
      largeToast = true
      if (editPart.type === "action") {
        msg = lang === "sv" ? "Uppgift skapad" : "Action created";
      } else if (editPart.type === "activity") {
        msg = lang === "sv" ? "Aktivitet skapad" : "Activity created";
      } else if (editPart.type === "goal") {
        msg = lang === "sv" ? "Delmål skapat" : "Sub-goal created";
      } else {
        return;
      }
    }

    if (largeToast) {
      setGlobalToast({
        position: "top-center",
        type: "info",
        infoType: "created",
        text: msg,
      });
    } else {
      setGlobalToast({text: msg});
    }
  }

  const saveVisionStatementPart = (title: string, description: string, deadline: string,
                                   barrier: string, ifWhen: string, ifThen: string, howOften: number) => {
    if (!editPart) {
      alert("No edit part!?");
      return;
    }
    const data = {
      cmd: "save",
      id: editPart.id,
      type: editPart.type,
      goalId: editPart.goalId ? editPart.goalId : "",
      title: title,
      description: description,
      if_when: ifWhen,
      if_then: ifThen,
      barrier: barrier,
      how_often: howOften,
      deadline: deadline,
    }

    api.post("client/vision_statement_update_part/" + visionStatement.id, data).then(response => {
      if (response.json.status === "ok") {
        showVisionStatementPartToast()
        setEditPart(null);
        getData();
        changeTab(0);
      }
    })
  }

  const updateVisionStatementPartStatus = (id: string, type: string, newStatus: number) => {
    const data = {
      cmd: "updateStatus",
      id: id,
      type: type,
      status: newStatus
    }

    api.post("client/vision_statement_update_part/" + visionStatement.id, data).then(response => {
      if (response.json.status === "ok") {
        if (newStatus === 1) {
          let msg = "?";
          if (type === "action") {
            msg = lang === "sv" ? "Uppgift slutförd!" : "Action completed!";
          } else if (type === "behavior") {
            msg = lang === "sv" ? "Beteende slutfört!" : "Behaviour completed!";
          } else if (type === "activity") {
            msg = lang === "sv" ? "Aktivitet slutförd!" : "Activity completed!";
          }
          setGlobalToast({
            position: "top-center",
            type: "info",
            infoType: "created",
            text: msg,
          });
        }
        setEditPart(null);
        getData();
      }
    })
  }

  const deleteVisionStatementPart = (id: string, type: string) => {
    const data = {
      id: id,
      type: type,
    }
    api.post("client/vision_statement_delete_part/" + visionStatement.id, data).then(response => {
      if (response.json.status === "ok") {
        if (deletePart && deletePart.type === "vision_statement") {
          navigate("/dev-plan");
        } else {
          setDeletePart(null);
          getData();
        }

      }
    })
  }

  const createFeedbackRequest = () => {
    if (visionStatement.ownSurvey) {
      navigate("/assessments/own/" + visionStatement.id);
    } else {
      api.post("client/survey_request/create", {
        survey_id: visionStatement.surveyId,
        vision_statement_id: visionStatement.id,
      }).then((response) => {
        if (response.json.status === "ok") {
          navigate("/feedback/survey/" + response.json.surveyRequestId);
        } else {
          alert("Error");
        }
      })
    }
  }

  const doUpdateGoalStatus = (development: number, engagement: number) => {
    if (!viewGoal) {
      alert("Error - no goal");
      return;
    }
    const data = {
      id: viewGoal.id,
      development: development,
      engagement: engagement,
    }
    api.post("client/vision_statement_goal_status/" + visionStatement.id, data).then(response => {
      if (response.json.status === "ok") {
        setGlobalToast({
          position: "top-center",
          type: "info",
          infoType: "created",
          text: lang === "sv" ? "Delmål slutfört" : "Sub-goal completed!",
        });
        setUpdateGoalStatus(false);
        getData();
      }
    })
  }

  const doUpdateProgress = (progress: number, development: number, effort: number, onTrack: number) => {
    const data = {
      progress: progress,
      development: development,
      effort: effort,
      onTrack: onTrack,
    }
    if (progress === 100) {
      setGlobalToast({
        position: "top-center",
        type: "info",
        infoType: "completed",
        text: lang === "sv" ? "Målbild slutförd!" : "Goal completed!",
      });
    }
    api.post("client/vision_statement_status/" + visionStatement.id, data).then(response => {
      if (response.json.status === "ok") {
        setUpdateProgress("");
        getData();
      }
    })
  }

  const reopenVisionStatement = () => {
    setEditPart({
      id: visionStatement.id,
      type: "vision_statement",
      title: visionStatement.title,
      description: visionStatement.description,
      deadline: visionStatement.deadline,
    });
  }

  const renderFollowUp = () => {
    if (visionStatement.surveyRequests.length === 0) {
      return;
    }
    const surveyRequest = visionStatement.surveyRequests[0];
    if (!surveyRequest.completed) {
      return (
        <div className="bg-indigo-900 mt-6 p-4 rounded-md relative border-indigo-800 border">
          <div style={{position: "absolute", top: -25, left: "calc(50% - 4px)"}}
               className="bg-indigo-900 w-1 h-6"></div>
          <div className="flex gap-x-4 items-center">
            <div className="rounded-sm p-2 bg-indigo-800 border-indigo-700 border">
              <img src={iconProgress} className="h-6 w-6" alt="lock"/>
            </div>
            <div className="flex items-center justify-between">
              <div className="z-heading2">{t("follow-up")}</div>
            </div>
          </div>
          {renderStepper(surveyRequest)}
        </div>
      )
    }

    const nextFeedbackDate = new Date(surveyRequest.created > visionStatement.activated ? surveyRequest.created : visionStatement.activated);
    nextFeedbackDate.setDate(nextFeedbackDate.getDate() + 90);
    const itIsTime = nextFeedbackDate < new Date();
    return (
      <div className="bg-indigo-900 mt-6 p-4 rounded-md relative border-indigo-800 border">
        <div style={{position: "absolute", top: -25, left: "calc(50% - 4px)"}} className="bg-indigo-900 w-1 h-6"></div>
        <div className="flex gap-x-4 items-center">
          <div className="rounded-sm p-2 bg-indigo-800 border-indigo-700 border">
            <img src={iconProgress} className="h-6 w-6" alt="lock"/>
          </div>
          <div className="flex items-center justify-between">
            <div className="z-heading2">{t("follow-up")}</div>
            {
              visionStatement.progress === 100 &&
                <img src={iconCheckCircleGreen} className="ml-4 h-6 w-6" alt="done"/>
            }
          </div>
        </div>

        {
          visionStatement.progress !== 100 &&
            <div>
              {
                itIsTime ? (
                  <>
                    <div className="mt-4 p-4 bg-indigo-800 border border-primary-500 rounded-md">
                      <div className="flex items-center gap-x-2">
                        <img src={iconInfoFilled} className="h-4 w-4" alt="info"/>
                        <div className="z-title3">
                          {lang === "sv" ? "Ta pulsen på din utveckling" : "Time for fresh feedback"}
                        </div>
                      </div>
                      <div className="z-body1">
                        {lang === "sv" ? "Det här är för dig - se var du står just nu" :
                          "This one's for you - see how far you've come"}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mt-4 z-body1">
                      {lang === "sv" ? "Se hur du utvecklas - ny feedback var 90:e dag" :
                        "Get fresh feedback every 90 days to see your progress"}
                    </div>
                    <div className="mt-2 flex items-center">
                      <img src={iconCalendar} className="h-4 w-4" alt="cal"/>
                      <div className="ml-1 z-body2">
                        {lang === "sv" ? "Nästa omgång" : "Next round"}: {nextFeedbackDate.toISOString().substring(0, 10)}
                      </div>
                    </div>
                  </>
                )
              }
                <CustomButton buttonType={itIsTime ? "blueBase" : "outlineWhiteBase"} className="mt-4"
                              disabled={coachView}
                              rightIcon={<img src={itIsTime ? iconRightBlack : iconRightWhite} alt="right"/>}
                              onClick={() => createFeedbackRequest()}
                >
                  {t("get-feedback")}
                </CustomButton>
            </div>
        }
      </div>
    )
  }

  const deleteBarrier = (_barrier: BarrierInterface) => {
    const _barriers: Array<BarrierInterface> = [];
    setShowDeleteBarrier(null);
    for (const _b of barriers) {
      if (_b.text === _barrier.text) {
        const data = {
          id: _barrier.text,
          type: "barrier"
        }
        api.post("client/vision_statement_delete_part/" + visionStatement.id, data).then();
      } else {
        _barriers.push(_b);
      }
    }
    setBarriers(_barriers);
  }

  const renderActions = () => {
    if (goals.length === 0 && activities.length === 0 && !visionStatement.barriersDone) {
      return (
        <div className="bg-indigo-900 mt-6 p-4 rounded-md flex gap-x-4 items-center relative border-indigo-800 border">
          <div style={{position: "absolute", top: -25, left: "calc(50% - 4px)"}}
               className="bg-indigo-900 w-1 h-6"></div>
          <div className="rounded-sm p-2 bg-indigo-800 border-indigo-700 border">
            <img src={iconLock} className="h-6 w-6" alt="lock"/>
          </div>
          <div>
            <div className="z-heading2">{lang === "sv" ? "Aktivera" : "Action"}</div>
            <div className="z-body1">
              {lang === "sv" ? "Slutför Få koll för att öppna det här steget" :
                "Complete Awareness to unlock this section"}
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="bg-indigo-900 mt-6 p-4 rounded-md relative border-indigo-800 border">
        <div style={{position: "absolute", top: -25, left: "calc(50% - 4px)"}} className="bg-indigo-900 w-1 h-6"></div>
        <div className="mb-4 flex justify-between items-center">
          <div className="flex gap-x-4 items-center">
            <div className="rounded-sm p-2 bg-indigo-800 border-indigo-700 border">
              <img src={iconLightning} className="h-6 w-6" alt="flash"/>
            </div>
            <div className="z-heading2">{lang === "sv" ? "Aktivera" : "Action"}</div>
          </div>
          <CustomButton buttonType="neutralBase" size="sm" style={{width: "unset"}} disabled={coachView}
                        onClick={() => setSelectAction(true)}
                        leftIcon={<img src={iconPlus2} alt="info" />} >
            {lang === "sv" ? "Skapa ny" : "Add action"}
          </CustomButton>
        </div>

        {
          (goals.length === 0 && activities.length === 0 && behaviors.length === 0 && barriers.length === 0) &&
            <div className="border rounded-sm border-indigo-600 border-dashed p-4">
                <p className="mt-4 text-center">
                  {lang === "sv" ? "Lägg till beteenden, aktiviteter eller delmål - och börja röra dig mot målet" :
                    "Start creating behaviours, activities or sub-goals to make progress on your goal" }
                </p>
                <CustomButton buttonType="blueBase" size="sm" style={{width: "unset"}} className="mt-4 mx-auto"
                              onClick={() => setSelectAction(true)} disabled={coachView}>
                  {lang === "sv" ? "Börja skapa" : "Create action"}
                </CustomButton>
            </div>
        }

        {
          [...behaviors].sort((a, b) => {
            // Complex! Sort items without deadline last if completed, else first
            // Sort completed with oldest deadline last
            // Sort ongoing with oldest deadline first
            if (a.status !== b.status) return a.status > b.status ? 1 : -1;
            if (a.status !== 0 && b.status === 0) return -1;
            if (a.status === 0 && b.status === 0) {
              // New rule: if due date is same sort by created date
              if (a.deadline === b.deadline) {
                return a.created > b.created ? 1 : -1
              }
              return a.deadline > b.deadline ? 1 : -1;
            } else {
              return a.deadline > b.deadline ? -1 : 1;
            }
          })
            .map((behavior, i) => {
              return (
                <div key={"behavior_" + i}>
                  <div className="group mt-2 py-4 pr-4 pl-7 bg-indigo-800 rounded-md hover:bg-indigo-700 border-indigo-700 border cursor-pointer relative"
                       onClick={() => {
                         setViewBehavior(behavior);
                         setViewGoal(null);
                         setViewActivity(null);
                       }}>
                    <div className="z-action-color bg-lavender-700"></div>
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="z-title1">
                          {behavior.title}
                        </div>
                        <div className="flex items-center">
                          {
                            behavior.status === VisionStatementPartStatus.Done &&
                              <img src={iconCheckCircleGreen} className="h-6 w-6 mr-2" alt="done"/>
                          }
                          <span className="z-title3">{t("due-date")}:&nbsp;</span>
                          <span className="text-sm text-gray-300">{behavior.deadline}</span>
                        </div>
                      </div>
                      {
                        behavior.barrier &&
                          <div className="mt-2 flex items-center gap-x-2">
                              <img src={iconArrowBent} className="h-4 w-4" alt="barrier"/>
                              <div className="border border-gray-400 rounded-full py-1 px-4 z-body3 inline-block">
                                {behavior.barrier}
                              </div>
                          </div>
                      }
                      {
                        (behavior.status === VisionStatementPartStatus.None && visionStatement.progress !== 100 && behavior.deadline <= todayDate) &&
                          <AttentionBox message={attentionTxt(lang, 'behavior', false)} />
                      }
                    </div>
                  </div>
                </div>
              )
            })
        }

        {
          [...goals].sort((a, b) => {
            // Complex! Sort items without deadline last if completed, else first
            // Sort completed with oldest deadline last
            // Sort ongoing with oldest deadline first
            if (a.status !== b.status) return a.status > b.status ? 1 : -1;
            if (a.status !== 0 && b.status === 0) return -1;
            if (a.status === 0 && b.status === 0) {
              // New rule: if due date is same sort by created date
              if (a.deadline === b.deadline) {
                return a.created > b.created ? 1 : -1
              }
              return a.deadline > b.deadline ? 1 : -1;
            } else {
              return a.deadline > b.deadline ? -1 : 1;
            }
          })
            .map((goal, i) => {
              return (
                <div key={"goal_" + i}>
                  <div className="group mt-2 py-4 pr-4 pl-7 bg-indigo-800 rounded-md hover:bg-indigo-700 border-indigo-700 border cursor-pointer relative"
                       onClick={() => {
                         setViewGoal(goal);
                         setViewActivity(null);
                         setViewBehavior(null);
                       }}>
                    <div className="z-action-color bg-royal-600"></div>
                    <div className="flex items-center justify-between">
                      <div className="z-title1">
                        {goal.title}
                      </div>
                      <div className="flex items-center">
                        {
                          goal.status === VisionStatementPartStatus.Done &&
                            <img src={iconCheckCircleGreen} className="h-6 w-6 mr-2" alt="done"/>
                        }
                        <span className="z-title3">{t("due-date")}:&nbsp;</span>
                        <span className="text-sm text-gray-300">{goal.deadline}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="text-gray-100 font-medium">{lang === "sv" ? "Uppgifter" : "Tasks"}: </span>
                      <span className="text-gray-300">
                      {goal.actions.filter(g => g.status === 1).length} {t("of")}&nbsp;
                        {goal.actions.length} {t("completed").toLowerCase()}
                    </span>
                    </div>
                    {
                      /* **Single-line alert inside vision Statement page sub-goal list** */
                      (() => {
                        if (goal.status !== VisionStatementPartStatus.None || visionStatement.progress === 100) return null;
                        const selfDue = goal.deadline <= todayDate;
                        const subsDue = goal.actions.some(goal => (goal.deadline <= todayDate && goal.status != 1))
                        if (selfDue && subsDue) {
                          return <AttentionBox message={attentionTxt(lang, 'subGoal', false)} />
                        }

                        return null;
                      })()}
                  </div>
                </div>
              )
            })
        }

        {
          [...activities].sort((a, b) => {
            // Complex! Sort items without deadline last if completed, else first
            // Sort completed with oldest deadline last
            // Sort ongoing with oldest deadline first
            if (a.status !== b.status) return a.status > b.status ? 1 : -1;
            if (a.status !== 0 && b.status === 0) return -1;
            if (a.status === 0 && b.status === 0) {
              // New rule: if due date is same sort by created date
              if (a.deadline === b.deadline) {
                return a.created > b.created ? 1 : -1
              }
              return a.deadline > b.deadline ? 1 : -1;
            } else {
              return a.deadline > b.deadline ? -1 : 1;
            }
          }).map((activity, i) => {
            return (
              <div key={"activity_" + i}>
                <div className="group mt-2 py-4 pr-4 pl-7 bg-indigo-800 rounded-md hover:bg-indigo-700 border-indigo-700 border cursor-pointer relative"
                     onClick={() => {
                       setViewActivity(activity);
                       setViewGoal(null)
                     }}>
                  <div className={"z-action-color " + (activity.fromChat ? "bg-peach-600" : "bg-orange-300")}></div>
                  <div className="flex items-center justify-between">
                    <div className="z-title1">{activity.title}</div>

                    <div className="flex items-center">
                      {
                        activity.status === VisionStatementPartStatus.Done &&
                          <img src={iconCheckCircleGreen} className="h-6 w-6 mr-2" alt="done"/>
                      }
                      <span className="z-title3">{t("due-date")}:&nbsp;</span>
                      <span className="text-sm text-gray-300">{activity.deadline}</span>
                    </div>
                  </div>
                  {/* **Single-line alert inside vision Statement page activites list** */}
                  {
                    (activity.status === VisionStatementPartStatus.None && visionStatement.progress !== 100 && activity.deadline <= todayDate) &&
                     <AttentionBox message={attentionTxt(lang, 'activity', activity.fromChat)} />
                  }
                </div>
              </div>
            )
          })
        }

        {
          barriers.filter(b => !b.used).map((barrier, index) => {
            const buttonType = index === 0 ? "blueBase" : "neutralBase";
            const rightIcon = index === 0 ? iconRightBlack : iconRightWhite;
            return (
              <div key={"select_barrier_" + index}
                   className="mt-2 flex flex-wrap items-center gap-4 justify-between rounded-md border border-indigo-600 border-dashed p-4">
                <div className="border border-gray-400 rounded-full py-1 px-4 z-body3 inline-block">
                  {barrier.text}
                </div>
                <div className="flex items-center justify-between">
                  <CustomButton buttonType={buttonType} style={{width: "unset"}}
                                disabled={coachView}
                                onClick={() => setEditPart({
                                  id: "0",
                                  type: "behavior",
                                  title: "",
                                  description: "",
                                  barrier: barrier.text,
                                  deadline: "",
                                })}
                                rightIcon={<img src={rightIcon} alt="right"/>}>
                    {t("create-behavior")}
                  </CustomButton>
                  <img src={iconClose} alt="delete"
                       onClick={() => coachView ? alert("Not allowed") : setShowDeleteBarrier(barrier)}
                       className="p-2 h-8 w-8 ml-4 rounded-full hover:bg-indigo-700 cursor-pointer"/>
                </div>

              </div>
            )
          })
        }

      </div>
    )
  }

  const saveAwareness = (_awareness: AwarenessInterface) => {
    api.post("client/awareness_save/" + visionStatementId, _awareness).then();
    setAwareness(_awareness);
  }

  const saveBarriers = (_barriers: Array<BarrierInterface>) => {
    const data = {
      barriers: _barriers
    }
    api.post("client/barriers_save/" + visionStatementId, data).then(response => {
      if (response.json.status === "ok") {
        setVisionStatement({...visionStatement, barriersDone: true});
      }
    });
    setBarriers(_barriers);
  }

  const setSnapshotDone = (_done: boolean) => {
    const data = {
      done: _done
    }
    api.post("client/snapshot_done/" + visionStatementId, data).then(response => {
      if (response.json.status === "ok") {
        setVisionStatement({...visionStatement, snapshotDone: _done});
      }
    });
  }

  const awarenessNextStep = (cta: string) => {
    if (cta === "barriers") {
      setBarriersOpen(true);
    } else if (cta === "editGoal") {
      reopenVisionStatement();
    }
  }

  const goalSnapshotNext = (next: string) => {
    if (next === "start") {
      navigate("/assessments/own/" + visionStatement.id);
    } else if (next === "completed") {
      setTab(1);
    } else if (next === "undo") {
      setSnapshotDone(false);
    } else if (next === "continue") {
      navigate("/assessments/own/" + visionStatement.id);
    }
  }

  const renderGoalSnapshot = () => {
    const disabled = !awareness;
    let next = "start";
    let nextText = lang === "sv" ? "Starta" : "Start";
    let completed = false;
    let started = false;
    if (!disabled) {

      if (visionStatement.surveyRequests.length > 0) {
        const _surveyRequest = visionStatement.surveyRequests[0];
        if (_surveyRequest.completed) {
          nextText = lang === "sv" ? "Visa" : "View";
          next = "completed";
          started = true;
          completed = true;
        } else {
          nextText = lang === "sv" ? "Fortsätt" : "Continue";
          next = "continue";
          started = true;
        }
      } else if (visionStatement.snapshotDone) {
        nextText = lang === "sv" ? "Ångra" : "Undo";
        next = "undo";
      }
    }

    return (
      <div
        className={"bg-indigo-800 mt-4 p-4 rounded-md hover:bg-indigo-700 border-indigo-700 border " + (disabled ? "opacity-50 pointer-events-none" : "")}>
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            {
              completed ? (
                <div className="rounded-sm p-2 bg-success-500 w-11 text-center">
                  <img src={iconCheck} alt="check" className="h-6 w-6"/>
                </div>
              ) : (
                <div className="rounded-sm p-2 bg-indigo-700 border border-indigo-600 w-11 z-title3 text-center">2</div>
              )
            }
            <div className="ml-3">
              <div className="flex items-center gap-x-2">
                <div className="z-title1">{lang === "sv" ? "Nuläge" : "Goal snapshot"}</div>
                {
                  (!completed && visionStatement.snapshotDone)  &&
                    <PlainBadge text={t("skipped")} color="gray" size="small"/>
                }
              </div>
              <div className="z-body1">
                {lang === "sv" ? "Få fatt i ditt nuläge, med input från andra" : "See where you stand, with input from others"}

              </div>
            </div>
          </div>
          {
            !disabled &&
              <div className="flex items-center gap-x-4">
                {
                  (!started && !visionStatement.snapshotDone) &&
                    <CustomButton buttonType="transparentBase" size="sm" style={{width: "unset"}}
                                  onClick={() => setSnapshotDone(true)}>
                      {lang === "sv" ? "Skippa" : "Skip"}
                    </CustomButton>
                }
                <CustomButton buttonType={visionStatement.snapshotDone ? "transparentBase" : "blueBase"}
                              size="sm" style={{width: "unset"}}
                              rightIcon={visionStatement.snapshotDone ? null : <img src={iconRight} alt="right"/>}
                              onClick={() => goalSnapshotNext(next)}>
                  {nextText}
                </CustomButton>
              </div>
          }


        </div>
      </div>
    )
  }

  const maybeRenderAttention = () => {
    const behind = visionStatement.timeProgress - visionStatement.progress

    if (behind < 25 || visionStatement.attention === "behind") return null;

    let message = behind.toFixed(0) + "%";
    if (lang === "sv") {
      message += " efter tidsplan - uppdatera din progress"
    } else {
      message += " behind timeline - update your progress"
    }
    return (
      <AttentionBox message={message} />
    )
  }

  const weeksLeft = () => {
    if (!visionStatement.deadline) {
      return null;
    }
    const deadline = new Date(visionStatement.deadline);
    const weeksLeft = (deadline.getTime() - today.getTime()) / (7 * 24 * 3600 * 1000);
    if (weeksLeft < 0) {
      return null;
    }
    let msg: string;
    if (weeksLeft > 0.5 && weeksLeft < 1.5) {
      msg = lang === "sv" ? "vecka kvar" : "week to due date";
    } else {
      msg = lang === "sv" ? "veckor kvar" : "weeks to due date";
    }
    return (
      <div className="mt-2 flex justify-end z-body3 text-gray-400!">
        {weeksLeft.toFixed(0)}&nbsp;{msg}
      </div>
    )
  }

  const renderSuccessPlan = () => {
    const barrierStepNumber = visionStatement.ownSurvey ? 3 : 2
    let barriersDisabled = !awareness;
    if (visionStatement.ownSurvey && !visionStatement.snapshotDone && visionStatement.surveyRequests.length === 0) {
      barriersDisabled = true;
    }
    return (
      <div className="flex flex-wrap mt-6 gap-4">
        <div className="main-half-col">
          <div className="bg-indigo-900 border-indigo-800 border p-4 rounded-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="rounded-sm p-2 bg-indigo-800 border-indigo-700 border">
                  <img src={iconEye} className="h-6 w-6" alt="awareness"/>
                </div>
                <div className="z-heading2 ml-4">
                  {lang === "sv" ? "Få koll" : "Awareness"}
                </div>
                {
                  visionStatement.barriersDone &&
                    <img src={iconCheckCircleGreen} className="h-6 w-6 ml-4" alt="done"/>
                }
              </div>
              {
                visionStatement.barriersDone &&
                  <img src={iconDown} alt="open" onClick={() => setHideAwareness(!hideAwareness)}
                       className={"h-6 w-6 hover:bg-indigo-700 cursor-pointer rounded-xl" + (hideAwareness ? "" : " rotate-180")}/>
              }
            </div>

            {
              !hideAwareness &&
                <>
                  <ClientAwareness awareness={awareness} save={saveAwareness} nextStep={awarenessNextStep}
                                   visionStatementActivated={visionStatement.activated}
                                   nextBarriers={!visionStatement.ownSurvey} readOnly={coachView}/>

                  {
                    visionStatement.ownSurvey && renderGoalSnapshot()
                  }

                  <ClientBarriers barriers={barriers} save={saveBarriers} stepNumber={barrierStepNumber}
                                  disabled={barriersDisabled}
                                  previousActivated={awareness?.created} readOnly={coachView}
                                  done={visionStatement.barriersDone} open={barriersOpen}/>
              </>
            }

          </div>

          {renderActions()}

          {renderFollowUp()}
        </div>

        <div className="main-half-col">
        <div className="bg-indigo-900 p-4 rounded-md border-indigo-800 border">
          <div className="flex justify-between">
            <div className="flex items-center gap-x-1">
              <img src={iconDevelopmentPlan} alt="bars" className="h-4 w-4"/>
              <div className="z-title3">Progress</div>
            </div>
            <div className="z-body3 text-gray-400!">{visionStatement.progress}%</div>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <div className="h-2 rounded-full bg-indigo-700 w-full">
              <div className={"h-2 rounded-full " + (visionStatement.timeProgress - visionStatement.progress > 25 ? "bg-orange-500" : "bg-aquamarine-500")}
                   style={{width: visionStatement.progress + "%"}}></div>
            </div>
          </div>

          <div className="mt-4 flex justify-between">
            <div className="flex items-center gap-x-1">
              <img src={iconClock} alt="bars" className="h-4 w-4"/>
              <div className="z-title3">{t("timeline")}</div>
            </div>
            <div className="z-body3 text-gray-400!">{visionStatement.timeProgress.toFixed(0)}%</div>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <div className="h-2 rounded-full bg-indigo-700 w-full">
              <div className="h-2 rounded-full bg-primary-500" style={{width: visionStatement.timeProgress + "%"}}></div>
            </div>
          </div>

          {weeksLeft()}

          {maybeRenderAttention()}

          <CustomButton buttonType="outlineWhiteBase" style={{width: "unset"}} className="mt-8"
                        disabled={coachView || visionStatement.progress === 100}
                        onClick={() => setUpdateProgress("update")}>
            {t("update-progress")}
          </CustomButton>
        </div>

        <div className="bg-indigo-900 mt-6 p-4 rounded-md border-indigo-800 border">

            <div className="flex items-center gap-x-1">
              <img src={iconNotes} alt="description" className="h-4 w-4"/>
              <div className="z-title3">{t("description")}</div>
            </div>

            <div className="z-body1">
              {visionStatement.description}
            </div>

            <div className="mt-4 flex items-center gap-x-1">
              <img src={iconCalendar} alt="description" className="h-4 w-4"/>
              <div className="z-title3">{t("due-date")}</div>
            </div>
            <div className="z-body1">
              {visionStatement.deadline}
            </div>

            {visionStatementChats.length > 0 && (
              <div className="mt-6 z-title3">
                {lang === "sv" ? "Chat-trådar" : "Previous threads"}
              </div>
            )}

            {
              visionStatementChats.map(visionStatementChat => (
                <div key={"vs_chat_" + visionStatementChat.id}>
                  <div
                    className="p-2 rounded-md mt-2 z-body1 cursor-pointer bg-indigo-800 hover:bg-indigo-700"
                    onClick={() => coachView ? alert("Not allowed") : navigate("/ai/" + visionStatementChat.id)}
                  >
                    {visionStatementChat.topic}
                  </div>
                </div>
              ))
            }
          </div>
        </div>

      </div>
    )
  }

  const renderStepper = (surveyRequest: VsSurveyRequest) => {

    return <RequestStatus status={surveyRequest.status} language={lang} multipleRequest={!!surveyRequest.surveyMultipleRecipientId}
                          surveyRequestId={surveyRequest.id} visionStatementId={visionStatementId} readOnly={coachView}
                          deadline={surveyRequest.deadline} partial={!!surveyRequest.ownSurvey}/>;
  }

  const renderFeedbackResult = () => {
    if (visionStatement.surveyRequests.length === 0) {
      return null;
    }
    const surveyRequest = visionStatement.surveyRequests[surveyRequestNr];
    const lastSurveyRequest = visionStatement.surveyRequests[0];

    let scoreSelf = 0;
    let scoreOther = 0;
    let selfNr = 0;
    let otherNr = 0;
    const surveyResult = surveyRequest.surveyResult;
    const questions: Array<SurveyQuestionInterface> = [];

    if (surveyRequest.surveyResult) {
      for (const focusArea of surveyResult.focusAreas) {
        for (const question of focusArea.questions) {
          questions.push(question);
          if (question.scoreSelf > 0) {
            scoreSelf += question.scoreSelf;
            selfNr++;
          }
          if (question.scoreOther > 0) {
            scoreOther += question.scoreOther;
            otherNr++;
          }
        }
      }
      if (selfNr) {
        scoreSelf /= selfNr;
      }
      if (otherNr) {
        scoreOther /= otherNr;
      }
    }

    const feedbackType = visionStatement.feedbackType !== undefined ?
      visionStatement.feedbackType :
      FeedbackType.FEEDBACK;

    let createNewDisabled = true;

    if (!coachView && (lastSurveyRequest.completed || today > new Date(lastSurveyRequest.deadline))) {
      createNewDisabled = false;
    }

    return (
      <>
          <div className="mb-4 flex justify-between">
            {
              visionStatement.surveyRequests.length > 1 ? (
                <select className="z-select small" style={{ width: "unset" }}
                        onChange={(e) => setSurveyRequestNr(parseInt(e.target.value))}
                        value={surveyRequestNr}>
                  {
                    visionStatement.surveyRequests.map((surveyRequest, i) => (
                      <option key={"sr_" + i} value={i}>
                        {surveyRequest.created.substring(0, 10)}
                      </option>
                    ))
                  }
                </select>
              ) : (
                <div>&nbsp;</div>
              )
            }

            <CustomButton
              buttonType="transparentBase"
              size="sm"
              disabled={createNewDisabled}
              onClick={() => createFeedbackRequest()}
              leftIcon={<img src={iconPlus2} alt="info" className="h-4 w-4 mr-1" />}
              style={{ width: "unset" }}
            >
              {lang === "sv" ? "Få ny feedback" : "Get new feedback"}
            </CustomButton>
          </div>


        {
          surveyRequest.completed &&
          <div className="bg-indigo-900 rounded-md p-4 border-indigo-800 border">
            <div className="flex justify-between">
              <div className="font-medium text-gray-100">{t("overview")}</div>
              <div className="flex items-center justify-center">
                <div className="h-4 w-4 rounded-full bg-peach-500" onClick={() => getData()}></div>
                <div className="text-sm ml-2 text-gray-100">{t("self-assessment")}</div>
                <div className="h-4 w-4 rounded-full bg-peach-300 ml-4"></div>
                <div className="text-sm ml-2 text-gray-100">Feedback</div>
              </div>
            </div>

            <div className="main-half-col small mt-2">
              <TwoBars value1={scoreSelf} value2={scoreOther} max={surveyResult.nrAnswerOptions}
                results1={scoreSelf.toFixed(1) + "/" + surveyResult.nrAnswerOptions}
                results2={scoreOther.toFixed(1) + "/" + surveyResult.nrAnswerOptions}
              />
            </div>

            <SurveyQuestionsResult questions={questions} lang={lang} surveyResult={surveyResult}
              clientName={clientName} feedbackType={feedbackType} />

          </div>
        }

        {(!surveyRequest.completed && surveyRequestNr === 0) && renderStepper(surveyRequest)}

        {
          !surveyRequest.completed && surveyRequestNr > 0 &&
          <div className="mt-4">
            {lang === "sv" ? "Denna feedbackförfrågan blev inte klar" : "This feedback round was not completed"}
          </div>
        }


        {
          (feedbackType !== FeedbackType.ONLY_SELF && surveyResult && (surveyRequest.completed || surveyResult.respondents.length > 0)) &&
          <div className="mt-4 bg-indigo-900 rounded-md p-4 mb-4 border-indigo-800 border">
            <div className="z-title1">
              {t("details")}
            </div>

            <div className="mt-2 flex items-center">
              <img src={iconEmail} alt="sent" className="h-4 w-4 mr-2" />
              <div className="z-title3">{t("request-sent")}:</div>
              <div className="text-sm text-gray-400">&nbsp;{surveyRequest.created.substring(0, 10)}</div>
            </div>

            <div className="mt-2 flex items-center">
              <img src={iconCalendar} alt="cal" className="h-4 w-4 mr-2" />
              <div className="z-title3">{t("result-created-at")}:&nbsp;</div>
              <div className="text-sm text-gray-400">
                {surveyRequest.completed ? surveyRequest.completed.substring(0, 10) : t("pending")}
              </div>
            </div>

            <div className="mt-2 flex items-center">
              <img src={iconEmailOpen} alt="open" className="h-4 w-4 mr-2" />
              <div className="z-title3">{t("received")}:</div>
              <div className="text-sm text-gray-400">
                &nbsp;{surveyResult.nrOtherResponses} {t("out-of")} {surveyResult.respondents.length}
              </div>
            </div>

            <div className="mt-2 flex items-center">
              <img src={iconAccount} alt="who" className="h-4 w-4 mr-2" />
              <div className="z-title3">{t("requested-from")}:&nbsp;</div>
              <div className="text-sm text-gray-400">
                {surveyResult.respondents.map((respondent, i) => (
                  <span key={"resp_" + i}>
                    {i > 0 ? ", " : ""}
                    {respondent.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        }
      </>
    )
  }

  const renderNextStep = () => {

    if (visionStatement.surveyRequests.length === 0) {
      return (
        <div className="mt-4 rounded-md border border-dashed border-indigo-700 p-4 text-center">
          <img src={iconScale} alt="calendar" className="mt-4 h-8 w-8 mx-auto"/>
          <div className="mt-4 text-gray-300">
            {
              lang === "sv" ? "Skapa en tydlig bild av ditt nuläge med självskattning och feedback. " +
                "Identifiera styrkor och vad du kan utveckla vidare." :
                "Understand your current state through self-assessment and feedback. " +
                "Uncover what matters and see where to grow."
            }
          </div>
          <CustomButton buttonType="blueBase" className="mt-4 mx-auto" style={{width: "unset"}}
                        onClick={() => createFeedbackRequest()}>
            {t("request-feedback")}
          </CustomButton>
        </div>
      )
    }

    const lastSurveyRequest = visionStatement.surveyRequests[0];

    const today = new Date();
    const deadline = new Date(lastSurveyRequest.deadline);

    if (!lastSurveyRequest.completed) {
      if (deadline < today) {
        return (
          <div className="mt-4 rounded-md border border-dashed border-indigo-700 p-4 text-center">
            <img src={iconScale} alt="calendar" className="mt-4 h-8 w-8 mx-auto"/>
            <div className="mt-4 text-gray-300">
              {
                lang === "sv" ? "Din feedbackförfrågan misslyckades." : "Your feedback request failed."
              }
            </div>
            <div className="mt-4 text-gray-300">
              {
                lang === "sv" ? "Skicka ny feedback-förfrågan." : "Send new feedback request."
              }
            </div>
          </div>
        )
      }
    }
    return null;
  }

  const renderFeedback = () => {
    return (
      <div className="mt-4">

        {renderFeedbackResult()}

        {
          visionStatement.feedbackType !== FeedbackType.ONLY_SELF && renderNextStep()
        }

      </div>
    )
  }

  const changeGoalStatus = () => {
    if (!viewGoal) {
      return;
    }
    if (viewGoal.status === 1) {
      updateVisionStatementPartStatus(
        viewGoal.id,
        "goal",
        0
      )
    } else {
      setUpdateGoalStatus(true);
    }
  }

  const renderSidePanel = () => {

    if (viewActivity) {
      let popItems = viewActivity?.status === 1 ? [
        {
          title: t("delete"),
          click: () => setDeletePart({id: viewActivity.id, type: "activity"}),
          img: <img src={iconTrash} alt="delete" className="h-6 w-6"/>,
        },
      ] : [
        {
          title: t("edit"),
          click: () => setEditPart({
            id: viewActivity.id,
            type: "activity",
            goalId: viewActivity.id,
            title: viewActivity.title,
            description: viewActivity.description,
            deadline: viewActivity.deadline,
          }),
          img: <img src={iconPencilBare} alt="edit" className="h-6 w-6"/>,
        },
        {
          title: t("delete"),
          click: () => setDeletePart({id: viewActivity.id, type: "activity"}),
          img: <img src={iconTrash} alt="delete" className="h-6 w-6"/>,
        },
      ];

      return (
        <SidePanel close={() => {
          setViewActivity(null);
        }}>
          <div className="z-side-panel-header pl-4 flex justify-between items-center">
            <Checkbox id="fake_done" label={viewActivity.status === 1 ? t("completed") : t("mark-as-complete")}
                      disabled={coachView}
                      onChange={() => updateVisionStatementPartStatus(
                        viewActivity.id,
                        "activity",
                        viewActivity.status === 1 ? 0 : 1
                      )} checked={viewActivity.status > 0}/>
            <div>
              {
                !coachView &&
                  <DotsPopup items={popItems} noHover={true} isVerticalDots={true}/>
              }
            </div>
          </div>

          <div className="z-side-panel-middle">

            <hr className="border-indigo-800 mt-4"/>
            {
              (viewActivity.status === 0 && visionStatement.progress !== 100 && viewActivity.deadline <= todayDate) &&
                <AttentionBox message={attentionTxt(lang, 'activity',  viewActivity.fromChat)} margin="m-4"/>
            }
            <div className="flex ml-4 mt-4 gap-x-2">
              <div className={"rounded-md h-12 w-12 flex items-center justify-center " + (viewActivity.fromChat ? "bg-peach-600" : "bg-orange-300")}>
                <img src={viewActivity.fromChat ? iconAi : iconNotesBlack} className="h-6 w-6" alt="ico"/>
              </div>
              <div>
                <div className={"text-sm text-medium text-" + (viewActivity.fromChat ? "peach-600" : "orange-300")}>
                  {viewActivity.fromChat ? "AI coaching" : t("activity")}
                </div>
                <div className="z-heading2">{viewActivity.title}</div>
              </div>
            </div>


            <div className="p-4">
              <div className="z-title3 text-gray-300">{t("description")}</div>
              <div className="text-gray-400 whitespace-pre-line">{viewActivity.description}</div>
              <div className="z-title3 text-gray-300 mt-4">{t("due-date")}</div>
              <div className="text-gray-400">{viewActivity.deadline}</div>
            </div>
          </div>
          <div className="mx-4 mt-1">
          <CustomButton buttonType="blueBase" disabled={coachView || viewActivity.status === 1}
                          onClick={() => toAiThread("vision_statement_activity", viewActivity.id)}
                          leftIcon={<img src={iconAi} alt="ai" className="h-4 w-4"/>}>
              {t("get-support-ai-assistant")}
            </CustomButton>
          </div>
        </SidePanel>
      )
    } else if (viewBehavior) {
      let popItems = viewBehavior?.status === 1 ? [
        {
          title: t("delete"),
          click: () => setDeletePart({id: viewBehavior.id, type: "behavior"}),
          img: <img src={iconTrash} alt="delete" className="h-6 w-6"/>,
        },
      ]: [
        {
          title: t("edit"),
          click: () => setEditPart({
            id: viewBehavior.id,
            type: "behavior",
            title: viewBehavior.title,
            ifWhen: viewBehavior.ifWhen,
            ifThen: viewBehavior.ifThen,
            howOften: viewBehavior.howOften,
            barrier: viewBehavior.barrier,
            description: "",
            deadline: viewBehavior.deadline,
          }),
          img: <img src={iconPencilBare} alt="edit" className="h-6 w-6"/>,
        },
        {
          title: t("delete"),
          click: () => setDeletePart({id: viewBehavior.id, type: "behavior"}),
          img: <img src={iconTrash} alt="delete" className="h-6 w-6"/>,
        },
      ];

      const showPractiseFrequency = (freq: number) => {
        if (freq === 2) {
          return lang === "sv" ? "En gång per vecka" : "Once a week";
        } else if (freq === 1) {
          return lang === "sv" ? "Flera gånger per vecka" : "Several times a week";
        } else {
          return lang === "sv" ? "Varje dag" : "Every day"
        }
      }

      return (
        <SidePanel close={() => {
          setViewBehavior(null);
        }}>
          <div className="z-side-panel-header pl-4 flex justify-between items-center">
            <Checkbox id="fake_done" label={viewBehavior.status === 1 ? t("completed") : t("mark-as-complete")}
                      disabled={coachView}
                      onChange={() => updateVisionStatementPartStatus(
                        viewBehavior.id,
                        "behavior",
                        viewBehavior.status === 1 ? 0 : 1
                      )} checked={viewBehavior.status > 0}/>
            <div>
              {
                !coachView &&
                  <DotsPopup items={popItems} noHover={true} isVerticalDots={true}/>
              }
            </div>
          </div>

          <div className="z-side-panel-middle">
            <hr className="border-indigo-800 mt-4"/>
            {
              (viewBehavior.status === 0 && viewBehavior.deadline <= todayDate) &&
                <AttentionBox message={attentionTxt(lang, 'behavior',  false)} margin="m-4"/>
            }
            <div className="flex ml-4 mt-4 gap-x-2">
              <div className="bg-lavender-700 rounded-md h-12 w-12 flex items-center justify-center">
                <img src={iconPuzzleBlack} className="h-6 w-6" alt="ico"/>
              </div>
              <div>
                <div className="text-sm text-medium text-lavender-700">{t("behavior")}</div>
                <div className="z-heading2">{viewBehavior.title}</div>
              </div>
            </div>

            <div className="p-4">

              {
                viewBehavior.barrier &&
                  <>
                      <div className="pt-4 z-title3 text-gray-300">{t("barrier")}</div>
                      <div className="mb-4 border border-gray-400 rounded-full py-1 px-4 z-body3 inline-block">
                        {viewBehavior.barrier}
                      </div>
                  </>
              }
              <div className="z-title3 text-gray-300">{t("if_when")}</div>
              <div className="text-gray-400 whitespace-pre-line">{viewBehavior.ifWhen}</div>

              <div className="mt-4 z-title3 text-gray-300">{t("if_then")}</div>
              <div className="text-gray-400 whitespace-pre-line">{viewBehavior.ifThen}</div>

              <div className="mt-4 z-title3 text-gray-300">
                {lang === "sv" ? "Övningsfrekvens" : "Practise frequency"}
              </div>
              <div className="text-gray-400 whitespace-pre-line">
                {showPractiseFrequency(viewBehavior.howOften)}
              </div>

              <div className="mt-4 z-title3 text-gray-300">{t("due-date")}</div>
              <div className="text-gray-400">{viewBehavior.deadline}</div>
            </div>
          </div>
          <div className="mx-4 mt-1">
          <CustomButton buttonType="blueBase" disabled={coachView || viewBehavior.status === 1}
                          onClick={() => toAiThread("vision_statement_behavior", viewBehavior.id)}
                          leftIcon={<img src={iconAi} alt="ai" className="h-4 w-4"/>}>
              {t("get-support-ai-assistant")}
            </CustomButton>
          </div>
        </SidePanel>
      )
    } else if (viewGoal) {
      if (viewAction) { 
        let popItems = viewAction?.status === 1 ?  [
          {
            title: t("delete"),
            click: () => setDeletePart({id: viewAction.id, type: "action"}),
            img: <img src={iconTrash} alt="delete" className="h-6 w-6"/>,
          },]:[
            {
            title: t("edit"),
            click: () => setEditPart({
              id: viewAction.id,
              type: "action",
              goalId: viewGoal.id,
              title: viewAction.title,
              description: viewAction.description,
              deadline: viewAction.deadline,
            }),
            img: <img src={iconPencilBare} alt="edit" className="h-6 w-6"/>,
          },
          {
            title: t("delete"),
            click: () => setDeletePart({id: viewAction.id, type: "action"}),
            img: <img src={iconTrash} alt="delete" className="h-6 w-6"/>,
          },
        ];

        return (
          <SidePanel close={() => {
            setViewGoal(null);
            setViewAction(null);
          }}>
            <div className="z-side-panel-header pl-4 flex justify-between items-center">
              <div className="flex items-center gap-x-4">
                <img src={iconLeft} alt="back" className="h-6 w-6 cursor-pointer" onClick={() => setViewAction(null)}/>
                <Checkbox id="fake_done" label={viewAction.status === 1 ? t("completed") : t("mark-as-complete")}
                          disabled={coachView}
                          onChange={() => updateVisionStatementPartStatus(
                            viewAction.id,
                            "action",
                            viewAction.status === 1 ? 0 : 1
                          )} checked={viewAction.status > 0}/>
              </div>
              <div>
                {
                  !coachView &&
                    <DotsPopup items={popItems} noHover={true} isVerticalDots={true}/>
                }
              </div>
            </div>

            <div className="z-side-panel-middle">
              <hr className="border-indigo-800 mt-4"/>
              {
                (viewAction.status === 0 && visionStatement.progress !== 100 && viewAction.deadline <= todayDate) &&
                  <AttentionBox message={attentionTxt(lang, 'action',  false)} margin="m-4"/>
              }
              <div className="flex ml-4 mt-4 gap-x-2">

                <div className="bg-royal-700 rounded-md h-12 w-12 flex items-center justify-center">
                  <img src={iconFlagBlack} className="h-6 w-6" alt="ico"/>
                </div>
                <div>
                  <div className="text-sm text-medium text-royal-700">{t("task")}</div>
                  <div className="z-heading2">{viewAction.title}</div>
                </div>
              </div>

              <div className="p-4">

                <div className="z-title3 mt-4 text-gray-300">{t("description")}</div>
                <div className="text-gray-400 whitespace-pre-line">{viewAction.description}</div>
                <div className="z-title3 text-gray-300 mt-4">{t("due-date")}</div>
                <div className="text-gray-400">{viewAction.deadline}</div>
              </div>
            </div>

            <div className="mx-4 mt-1">
              <CustomButton buttonType="blueBase" disabled={coachView || viewAction.status === 1}
                            onClick={() => toAiThread("vision_statement_action", viewAction.id)}
                            leftIcon={<img src={iconAi} alt="ai" className="h-4 w-4"/>}>
                {t("get-support-ai-assistant")}
              </CustomButton>
            </div>
          </SidePanel>
        )
      } else {
        let popItems = viewGoal.status === 1 ? [
          {
            title: t("delete"),
            click: () => setDeletePart({id: viewGoal.id, type: "goal"}),
            img: <img src={iconTrash} alt="delete" className="h-6 w-6"/>,
          },]:[{
            title: t("edit"),
            click: () => setEditPart({
              id: viewGoal.id,
              type: "goal",
              title: viewGoal.title,
              description: viewGoal.description,
              deadline: viewGoal.deadline,
            }),
            img: <img src={iconPencilBare} alt="edit" className="h-6 w-6"/>,
          },
          {
            title: t("delete"),
            click: () => setDeletePart({id: viewGoal.id, type: "goal"}),
            img: <img src={iconTrash} alt="delete" className="h-6 w-6"/>,
          },
        ];

        return (
          <SidePanel close={() => {
            setViewGoal(null);
          }}>
            <div className="z-side-panel-header pl-4 flex justify-between items-center">
              <div>
                <Checkbox id="fake_done" disabled={coachView}
                          label={viewGoal.status === 1 ? t("completed") : t("mark-as-complete")}
                          onChange={() => changeGoalStatus()} checked={viewGoal.status > 0}/>
              </div>
              <div>
                {
                  !coachView &&
                    <DotsPopup items={popItems} noHover={true} isVerticalDots={true}/>
                }

              </div>
            </div>
            <div className="z-side-panel-middle">
              <hr className="border-indigo-800 mt-4"/>
              {
                (viewGoal.status === 0 && visionStatement.progress !== 100 && viewGoal.deadline <= todayDate) &&
                  <AttentionBox message={attentionTxt(lang, 'subGoal',  false)} margin="m-4"/>
              }
              <div className="flex ml-4 mt-4 gap-x-2">
                <div className="bg-royal-700 rounded-md h-12 w-12 flex items-center justify-center">
                  <img src={iconFlagBlack} className="h-6 w-6" alt="ico"/>
                </div>
                <div>
                  <div className="text-sm text-medium text-royal-700">{t("sub-goal")}</div>
                  <div className="z-heading2">{viewGoal.title}</div>
                </div>
              </div>
              <div className="p-4">
                <div className="z-title3 mt-4 text-gray-300">{t("description")}</div>
                <div className="text-gray-400 whitespace-pre-line">{viewGoal.description}</div>

                <div className="z-title3 text-gray-300 mt-4">{t("due-date")}</div>
                <div className="text-gray-400">{viewGoal.deadline}</div>

                <div className="mt-4 z-title3 text-gray-100">{t("behaviors-and-actions")}</div>

                {
                  [...viewGoal.actions].sort((a, b) => {
                    // Complex! Sort items without deadline last if completed, else first
                    // Sort completed with oldest deadline last
                    // Sort ongoing with oldest deadline first

                    if (a.status !== b.status) return a.status > b.status ? 1 : -1;
                    if (a.status !== 0 && b.status === 0) return -1;
                    if (a.status === 0 && b.status === 0) {
                      // New rule: if due date is same sort by created date 
                      if (a.deadline === b.deadline) {
                        return a.created > b.created ? 1 : -1;
                      }
                      return a.deadline > b.deadline ? 1 : -1;
                    } else {
                      return a.deadline > b.deadline ? -1 : 1;
                    }
                  }).map(action => (
                    <div className="group mt-4 rounded-md p-4 bg-indigo-900 cursor-pointer hover:bg-indigo-800"
                         key={"action_" + action.id}
                         onClick={() => setViewAction(action)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-x-2 z-title1">
                          <div className="">{action.title}</div>
                          {
                            action.status === VisionStatementPartStatus.Done &&
                              <PlainBadge text={t("completed")} color="green" iconLeft={iconCheck} size="small"/>
                          }
                        </div>
                        <div className="font-medium text-gray-100">
                          <span className="z-title3  text-gray-100">{t("due-date")}: </span>
                          <span className="text-sm text-gray-300">{action.deadline}</span>
                        </div>
                      </div>
                      {/* **Line Alert Inside Sub-Goals Page: Behaviors and Actions** */}
                      {
                        (action.status === 0 && visionStatement.progress !== 100 && action.deadline <= todayDate) &&
                          <AttentionBox message={attentionTxt(lang, 'activity',  false)}/>
                      }
                    </div>
                  ))
                }

                {
                  viewGoal.actions.length > 0 ?
                    (
                      <div className="mt-4 text-center">
                        <CustomButton
                          buttonType="transparentBase"
                          className="mt-4 mx-auto cursor-pointer"
                          style={{width: "unset"}}
                          leftIcon={<img src={iconPlus2} alt="info"/>}
                          disabled={viewGoal.status === 1}
                          onClick={() => setEditPart({
                            id: "0",
                            type: "action",
                            goalId: viewGoal.id,
                            title: "",
                            description: "",
                            deadline: ""
                          })}>
                          {lang === "sv" ? "Lägg till" : "Add new"}
                        </CustomButton>
                      </div>
                    ) : (
                      <div className="mt-4 rounded-md border border-dashed border-indigo-700 p-4 text-center">
                        <img src={iconCalendar2} alt="calendar" className="mt-4 h-8 w-8 mx-auto"/>
                        <div className="mt-4 text-gray-300">
                          {
                            lang === "sv" ? "Bryt ner ditt delmål i små, konkreta steg." :
                              "Take small steps to reach your sub-goal."
                          }
                        </div>
                        <CustomButton buttonType="blueBase" className="mt-4 mx-auto" style={{width: "unset"}}
                                      disabled={viewGoal.status === 1}
                                      onClick={() => setEditPart({
                                        id: "0",
                                        type: "action",
                                        goalId: viewGoal.id,
                                        title: "",
                                        description: "",
                                        deadline: ""
                                      })}>
                          + {lang === "sv" ? "Lägg till" : "Add new"}
                        </CustomButton>
                      </div>
                    )
                }

              </div>
            </div>

            <div className="mx-4 mt-1">
              <CustomButton buttonType={viewGoal.actions.length > 0 ? "blueBase" : "outlineWhiteBase"}
                            onClick={() => toAiThread("vision_statement_goal", viewGoal.id)}
                            leftIcon={<img src={iconAi} alt="ai" className="h-4 w-4"/>}
                            disabled={viewGoal.status === 1}>
                {t("get-support-ai-assistant")}
              </CustomButton>
            </div>

          </SidePanel>
        )
      }
    }

  }

  const startVoice = () => {
    storeAiStart(coachStyle, "voice", null, null, null, null);
    navigate("/ai/0");
  };

  const renderCreated = () => {
    const items = [
      {
        title: t("overview"),
        callback: () => changeTab(0),
      },
      {
        title: "Feedback",
        callback: () => changeTab(1),
      }
    ]

    return (
      <>
        <div className="mt-6 flex flex-row justify-between items-center gap-y-2">
          <div className="flex flex-row md:justify-between gap-x-2 items-center">
            <div className="bg-indigo-900 rounded-sm p-2">
              <img src={iconCircleRings} alt="rings" className="h-8 w-8"/>
            </div>
            <div className="z-h2">{visionStatement.title}</div>
            {
              visionStatement.progress === 100 ? (
                 <PlainBadge text={t("completed")} color="green" iconLeft={iconCheck} size="small"/>
              ) : (
                visionStatement.attention ? renderOnTrack2(visionStatement.attention, lang) : <></>
              )
            }
            
          </div>
          {
            !coachView &&
              <div>
                {visionStatement.progress === 100 ? (
                  <DotsPopup isVerticalDots={true} items={[
                    {
                      title: t("mark-as-incomplete"),
                      click: () => setUpdateProgress(visionStatement.progress === 100 ? "update" : "done"),
                      img: <img src={iconCheckCircle} alt="edit" className="h-4 w-4"/>,
                    },
                    {
                      title: t("delete"),
                      click: () => setDeletePart({type: "vision_statement", id: "0"}),
                      img: <img src={iconTrash} alt="edit" className="h-4 w-4"/>,
                    }
                  ]}/>
                ):(
                  <DotsPopup isVerticalDots={true} items={[
                    {
                      title: lang === "sv" ? "Ändra" : "Edit",
                      click: () => reopenVisionStatement(),
                      img: <img src={iconPencilBare} alt="edit" className="h-4 w-4"/>,
                    },
                    {
                      title: t("mark-as-complete"),
                      click: () => setUpdateProgress(visionStatement.progress === 100 ? "update" : "done"),
                      img: <img src={iconCheckCircle} alt="edit" className="h-4 w-4"/>,
                    },
                    {
                      title: t("delete"),
                      click: () => setDeletePart({type: "vision_statement", id: "0"}),
                      img: <img src={iconTrash} alt="edit" className="h-4 w-4"/>,
                    }
                  ]}/>
                )}
              </div>
          }
        </div>

        {
          visionStatement.progress < 10 ? (
            <div className="mt-8 text-gray-400">
              <CustomButton buttonType="blueBase" className="mt-4" onClick={() => reopenVisionStatement()}>
                {lang === "sv" ? "Slutför din målbild" : "Finalize your Goal"}
              </CustomButton>
            </div>
          ) : (
            <>
              <div className="mt-6">
                <TabMenu
                  active={tab}
                  items={items}
                />
              </div>

              {tab === 0 && renderSuccessPlan()}
              {tab === 1 && renderFeedback()}

              {(viewGoal || viewActivity || viewBehavior) && renderSidePanel()}

              {
                updateProgress.length > 0 &&
                  <UpdateProgress visionStatement={visionStatement} close={() => setUpdateProgress("")}
                                  type={updateProgress}
                                  lang={lang} save={doUpdateProgress} t={t}/>
              }

              {
                (updateGoalStatus && viewGoal) &&
                  <UpdateGoalStatus goal={viewGoal} close={() => setUpdateGoalStatus(false)}
                                    lang={lang} save={doUpdateGoalStatus} t={t}/>
              }

              {
                (tab === 0 && !coachView) &&
                  <>
                    <div className="z-chat-bottom ml-0 md:ml-16 ">
                      <div className="z-chat-bottom-box bg-indigo-900">
                        <ChatThreadInput
                          sendToOtherPage={true}
                          handleSendMessage={(msg) => vsChat(coachStyle, "", msg)}
                          onHeightChange={() => {
                          }}
                          setCoachStyle={setCoachStyle}
                          coachStyle={coachStyle}
                          startVoice={startVoice}
                          placeholder={
                            lang === "sv"
                              ? "Skriv något för att starta en coachsession på målbilden..."
                              : "Ask anything to start a coaching session on your Goal..."
                          }
                          emmaThinking={false}
                        />
                      </div>
                    </div>
                    {/* Insert space so that bottom search box doesn't cover bottom content */}
                    <div className="h-32">&nbsp;</div>
                  </>
              }
            </>
          )
        }

      </>
    )
  }

  const renderEditPart = () => {
    if (editPart) {
      const save = editPart.type === "vision_statement" ? saveVisionStatement : saveVisionStatementPart
      return (
        <LongTermGoalPart visionStatement={visionStatement}
                          editPart={editPart} viewGoal={viewGoal} clientName={clientName} save={save}
                          close={() => setEditPart(null)}/>
      )
    }
  }

  const renderDeletePart = () => {
    if (deletePart) {
      return (
        <Modal isOpen={true} onClose={() => setDeletePart(null)}>
          <ModalHeader onClose={() => setDeletePart(null)}>
            {t("delete")}?
          </ModalHeader>
          <ModalBody>
            {lang === "sv" ? "Detta går inte att ångra." : "This can not be undone."}
            {
              deletePart.type === "vision_statement" &&
              <>
              {
                lang === "sv" ? " Allt du har gjort inom detta delmål kommer att raderas." :
                   "Everything within this Goal will be deleted."}
              </>
            }
          </ModalBody>
          <ModalFooter>
            <div className="flex gap-x-4 w-full">
              <div className="w-1/2">
                <CustomButton buttonType="outlineWhiteBase" onClick={() => setDeletePart(null)}>
                  Cancel
                </CustomButton>
              </div>
              <div className="w-1/2">
                <CustomButton buttonType="dangerBase"
                              onClick={() => deleteVisionStatementPart(deletePart.id, deletePart.type)}>
                  Delete
                </CustomButton>
              </div>
            </div>
          </ModalFooter>
        </Modal>
      )
    }
  }

  const showSelectAction = () => {
    return (
      <Modal isOpen={true} onClose={() => setSelectAction(false)}>
        <ModalHeader onClose={() => setSelectAction(false)}>
          {lang === "sv" ? "Välj vad du vill lägga till" : "Choose an action"}
        </ModalHeader>
        <ModalBody>
          <div className="border border-lavender-700 rounded-md flex hover:bg-indigo-800 cursor-pointer"
               onClick={() => {
                 setSelectAction(false);
                 setEditPart({
                   id: "0",
                   type: "behavior",
                   title: "",
                   description: "",
                   deadline: "",
                   barriers: barriers
                 })
               }}>
            <div className="bg-lavender-700 w-14 flex items-center justify-center">
              <img src={iconPuzzleBlack} className="h-6 w-6" alt="ico"/>
            </div>
            <div className="p-4">
              <div className="z-title2">{t("behavior")}</div>
              <div className="z-body2">
                {lang === "sv" ? "Träna på ett beteende i vardagen" : "Practice a behaviour you want to make click"}
              </div>
            </div>
          </div>

          <div className="mt-2 border border-orange-300 rounded-md flex hover:bg-indigo-800 cursor-pointer"
               onClick={() => {
                 setSelectAction(false);
                 setEditPart({
                   id: "0",
                   type: "activity",
                   title: "",
                   description: "",
                   deadline: ""
                 })
               }}>
            <div className="bg-orange-300 w-14 flex items-center justify-center">
              <img src={iconNotesBlack} className="h-6 w-6" alt="activity"/>
            </div>
            <div className="p-4">
              <div className="z-title2">{t("activity")}</div>
              <div className="z-body2">
                {lang === "sv" ? "En konkret sak du kan bocka av" : "Take a one-off step toward your goal"}
              </div>
            </div>
          </div>

          <div className="mt-2 border border-royal-600 rounded-md flex hover:bg-indigo-800 cursor-pointer"
               onClick={() => {
                 setSelectAction(false);
                 setEditPart({
                   id: "0",
                   type: "goal",
                   title: "",
                   description: "",
                   deadline: ""
                 })
               }}>
            <div className="bg-royal-600 w-14 flex items-center justify-center">
              <img src={iconFlagBlack} className="h-6 w-6" alt="activity"/>
            </div>
            <div className="p-4">
              <div className="z-title2">{t("sub-goal")}</div>
              <div className="z-body2">
                {lang === "sv" ? "Dela upp din målbild i mindre delar" : "Break your goal into smaller goals"}
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    )
  }

  const renderDeleteBarrier = () => {
    if (showDeleteBarrier) {
      return (
        <Modal isOpen={true} onClose={() => setShowDeleteBarrier(null)}>
          <ModalHeader onClose={() => setShowDeleteBarrier(null)}>
            {lang === "sv" ? "Radera hinder?" : "Delete barrier?"}
          </ModalHeader>
          <ModalBody>
            <div className="z-body1">
              {
                lang === "sv" ? "Du kan alltid redigera hinder senare" : "You can always edit your barriers later"
              }
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="flex gap-x-2 w-full">
              <CustomButton buttonType="outlineWhiteBase" onClick={() => setShowDeleteBarrier(null)}>
                {t("cancel")}
              </CustomButton>
              <CustomButton buttonType="blueBase" onClick={() => deleteBarrier(showDeleteBarrier)}>
                {t("delete")}
              </CustomButton>
            </div>
          </ModalFooter>
        </Modal>
      )
    }
  }

  return (
    <div className="main-one-col">

      {
        coachView ? (
          <LocalBreadcrumbs crumbs={[
            {name: lang === "sv" ? "Hem" : "Home", url: "/"},
            {name: t("my-clients"), url: "/clients"},
            {name: clientName, url: "/clients/" + clientId},
            {name:t("vision_statement")},
          ]}/>
        ) : (
          <LocalBreadcrumbs crumbs={[
            {name: t("long-term-goals"), url: "/dev-plan"},
            {name: t("vision_statement")},
          ]}/>
        )
      }

      {
        renderCreated()
      }
      {
        renderEditPart()
      }
      {
        deletePart && renderDeletePart()
      }
      {
        selectAction && showSelectAction()
      }
      {
        showDeleteBarrier && renderDeleteBarrier()
      }

    </div>
  )
}

export default LongTermGoal;